/**
 * Structured logging utility for LawAssist
 * Replaces all console.log statements with structured, searchable logs
 * 
 * @module lib/logging
 */

import type { AssistantType } from '@/shared/types/database.types';

/** Log severity levels */
export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/** Context object for structured logging */
export interface LogContext {
  /** Which assistant triggered the log */
  assistantType?: AssistantType;
  /** Business client ID for arbitration */
  businessId?: string;
  /** Court ID for conciliation */
  courtId?: string;
  /** Category ID for suit filing */
  categoryId?: string;
  /** Process/case ID */
  processId?: string;
  /** User ID (when auth is implemented) */
  userId?: string;
  /** Organization ID */
  organizationId?: string;
  /** Additional metadata */
  [key: string]: unknown;
}

/** Log entry structure */
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  action: string;
  context: LogContext;
  message?: string;
}

/** Environment check for development mode */
const isDev = import.meta.env?.DEV ?? true;

/** Log level priority for filtering */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

/** Minimum log level to output (configurable via env) */
const MIN_LOG_LEVEL: LogLevel = isDev ? 'DEBUG' : 'INFO';

/**
 * Formats a log entry for console output
 */
function formatLogEntry(entry: LogEntry): string {
  const { timestamp, level, action, context, message } = entry;
  const contextStr = Object.keys(context).length > 0 
    ? ` | ${JSON.stringify(context)}` 
    : '';
  const messageStr = message ? ` - ${message}` : '';
  return `[${timestamp}] [${level}] ${action}${messageStr}${contextStr}`;
}

/**
 * Determines if a log should be output based on level
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LOG_LEVEL];
}

/**
 * Main logging function - use for all application logs
 * 
 * @param level - Severity level (DEBUG, INFO, WARN, ERROR)
 * @param action - Action identifier (e.g., 'FETCH_COURTS', 'CREATE_PROCESS')
 * @param context - Structured context object
 * @param message - Optional human-readable message
 * 
 * @example
 * log('INFO', 'FETCH_COURTS_SUCCESS', { courtId: 'court-1' }, 'Loaded 5 courts');
 */
export function log(
  level: LogLevel,
  action: string,
  context: LogContext = {},
  message?: string
): void {
  if (!shouldLog(level)) return;

  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    action,
    context,
    message,
  };

  const formatted = formatLogEntry(entry);

  switch (level) {
    case 'ERROR':
      console.error(formatted);
      break;
    case 'WARN':
      console.warn(formatted);
      break;
    case 'DEBUG':
      console.debug(formatted);
      break;
    default:
      console.info(formatted);
  }

  // TODO [PHASE 3]: Send logs to remote logging service (e.g., Supabase, DataDog)
}

/**
 * Specialized error logging function
 * Extracts error details and logs with full context
 * 
 * @param action - Action that failed (e.g., 'FETCH_COURTS')
 * @param error - The caught error object
 * @param context - Additional context
 * 
 * @example
 * try {
 *   await fetchCourts();
 * } catch (err) {
 *   logError('FETCH_COURTS', err, { organizationId: 'org-1' });
 *   throw new Error('Failed to load courts. Please try again.');
 * }
 */
export function logError(
  action: string,
  error: unknown,
  context: LogContext = {}
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  log('ERROR', `${action}_FAILED`, {
    ...context,
    errorMessage,
    errorStack: isDev ? errorStack : undefined, // Only include stack in dev
  });
}

/**
 * Convenience functions for common log levels
 */
export const logger = {
  debug: (action: string, context?: LogContext, message?: string) => 
    log('DEBUG', action, context, message),
  
  info: (action: string, context?: LogContext, message?: string) => 
    log('INFO', action, context, message),
  
  warn: (action: string, context?: LogContext, message?: string) => 
    log('WARN', action, context, message),
  
  error: (action: string, error: unknown, context?: LogContext) => 
    logError(action, error, context),
};

/**
 * Creates a logger scoped to a specific assistant
 * Useful for per-assistant logging without repeating context
 * 
 * @param assistantType - The assistant type to scope to
 * 
 * @example
 * const arbLogger = createAssistantLogger('ARBITRATION');
 * arbLogger.info('FETCH_PIPELINES', { businessId: 'bus-1' });
 */
export function createAssistantLogger(assistantType: AssistantType) {
  return {
    debug: (action: string, context?: Omit<LogContext, 'assistantType'>, message?: string) =>
      log('DEBUG', action, { ...context, assistantType }, message),
    
    info: (action: string, context?: Omit<LogContext, 'assistantType'>, message?: string) =>
      log('INFO', action, { ...context, assistantType }, message),
    
    warn: (action: string, context?: Omit<LogContext, 'assistantType'>, message?: string) =>
      log('WARN', action, { ...context, assistantType }, message),
    
    error: (action: string, error: unknown, context?: Omit<LogContext, 'assistantType'>) =>
      logError(action, error, { ...context, assistantType }),
  };
}

// Pre-configured assistant loggers
export const arbitrationLogger = createAssistantLogger('ARBITRATION');
export const conciliationLogger = createAssistantLogger('CONCILIATION');
export const suitFilingLogger = createAssistantLogger('SUIT_FILING');
